class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';
    this.pageBody = document.getElementsByTagName('body')[0];

    this.setupEventListeners();
    console.log('predictive search loaded');
  }

  setupEventListeners() {
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));
    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      // Remove the results when they are no longer relevant for the new search term
      // so they don't show up when the dropdown opens again
      this.querySelector('#predictive-search-results-groups-wrapper')?.remove();
    }

    // Update the term asap, don't wait for the predictive search query to finish loading
    this.updateSearchForTerm(this.searchTerm, newSearchTerm);

    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    this.saveLastSearched();
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) event.preventDefault();
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = '';
      this.abortController.abort();
      this.abortController = new AbortController();
      this.closeResults(true);
    }
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) return;

    if (this.searchTerm !== currentSearchTerm) {
      // Search term was changed from other search input, treat it as a user change
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up');
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') {
      event.preventDefault();
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector('[data-predictive-search-search-for-text]');
    const currentButtonText = searchForTextElement?.innerText;
    if (currentButtonText) {
      if (
        currentButtonText.match(new RegExp(previousTerm, 'g')) &&
        currentButtonText.match(new RegExp(previousTerm, 'g')).length > 1
      ) {
        // The new term matches part of the button text and not just the search term, do not replace to avoid mistakes
        return;
      }
      const newButtonText = currentButtonText.replace(previousTerm, newTerm);
      searchForTextElement.innerText = newButtonText;
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');

    // Filter out hidden elements (duplicated page and article resources) thanks
    // to this https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
    const allVisibleElements = Array.from(this.querySelectorAll('li, button.predictive-search__item')).filter(
      (element) => element.offsetParent !== null
    );
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    this.statusElement.textContent = '';

    if (!moveUp && selectedElement) {
      activeElementIndex = selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex = selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedOption = this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]');

    if (selectedOption) selectedOption.click();
  }

  getSearchResults(searchTerm) {
    const queryKey = searchTerm.replace(' ', '-').toLowerCase();
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    fetch(
      `${routes.predictive_search_url}?q=${encodeURIComponent(
        searchTerm
      )}&section_id=predictive-search&resources[type]=product,collection&resources[limit]=9&resources[limit_scope]=each&resources[options][fields]=title,product_type,variants.title,vendor,variants.sku`,
      {
        signal: this.abortController.signal,
      }
    )
      .then((response) => {
        if (!response.ok) {
          var error = new Error(response.status);
          this.close();
          throw error;
        }

        return response.text();
      })
      .then((text) => {
        const resultsMarkup = new DOMParser()
          .parseFromString(text, 'text/html')
          .querySelector('#shopify-section-predictive-search').innerHTML;
        // Save bandwidth keeping the cache in all instances synced
        this.allPredictiveSearchInstances.forEach((predictiveSearchInstance) => {
          predictiveSearchInstance.cachedResults[queryKey] = resultsMarkup;
        });
        this.renderSearchResults(resultsMarkup);

        if (typeof publish !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
          const parser = new DOMParser();
          const doc = parser.parseFromString(resultsMarkup, 'text/html');
          const products = Array.from(doc.querySelectorAll('#predictive-search-results-products-list .predictive-search__list-item'));
          const collections = Array.from(doc.querySelectorAll('#predictive-search-results-queries-list .predictive-search__list-item'));
          const productHandles = products.map(p => p.querySelector('[data-product-handle]')?.getAttribute('data-product-handle')).filter(Boolean);

          publish(PUB_SUB_EVENTS.searchQuery, {
            query: searchTerm,
            search_context: 'overlay',
            engine: 'shopify',
            products_count: products.length,
            collections_count: collections.length,
            result_product_ids: productHandles
          });
        }
      })
      .catch((error) => {
        if (error?.code === 20) {
          // Code 20 means the call was aborted
          return;
        }
        this.close();
        throw error;
      });
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText;

    setTimeout(() => {
      this.statusElement.setAttribute('aria-hidden', 'true');
    }, 1000);
    this.mobileTabs();
    this.highlightSearchedWord();
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute('results', true);

    this.setLiveRegionResults();
    this.displayLastSearched();
    this.clearLastSearched();
    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    this.setLiveRegionText(this.querySelector('[data-predictive-search-live-region-count-value]').textContent);
  }

  open() {
    this.predictiveSearchResults.style.maxHeight = 'calc(100svh - var(--header-search-panel-height))';
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
    this.pageBody.classList.add('is_fixed');
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
    if (this.pageBody.classList.contains('is_fixed')) {
      this.pageBody.classList.remove('is_fixed');
    }
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.predictiveSearchResults.removeAttribute('style');
  }

  highlightSearchedWord() {
    const currval = this.input.value;
    const onlyLower = currval === currval.toLowerCase();
    const filterItems = this.querySelectorAll('[data-highlight]');
    for (let i = 0; i < filterItems.length; i++) {
      const textcontent = filterItems[i].textContent;
      const finalval = textcontent.replace(new RegExp(`(${currval})`, 'i'), function (_, p1) {
        if (onlyLower) {
          return '<strong>' + p1 + '</strong>';
        } else {
          return '<strong>' + currval + '</strong>';
        }
      });
      filterItems[i].innerHTML = finalval;
    }
  }

  mobileClick(target, tabContents, tabTriggers) {
    const tabId = target.getAttribute('data-mobile-tab');
    Array.from(tabTriggers).forEach((tab) => {
      tab.classList.remove('active');
    });

    Array.from(tabContents).forEach((tab) => {
      tab.style.display = 'none';
    });

    document.getElementById(tabId).style.display = 'flex';
    target.classList.add('active');
  }

  mobileTabs() {
    if (window.matchMedia('(max-width: 749px)').matches) {
      const tabContents = document.getElementsByClassName('mobile-tab__content');
      const tabTriggers = document.querySelectorAll('.tab');

      tabTriggers.forEach((tabTrigger) => {
        tabTrigger.addEventListener('click', () => {
          this.mobileClick(event.currentTarget, tabContents, tabTriggers);
          // event.currentTarget.classList.remove('active');
        });
      });
    }
  }

  saveLastSearched() {
    let formSearchValues = [];
    const storedSearchValues = localStorage.getItem('lastSearched');
    formSearchValues = storedSearchValues ? JSON.parse(storedSearchValues) : [];
    const inputValue = this.input.value;
    formSearchValues.push(inputValue);
    if (formSearchValues.length > 5) {
      formSearchValues = formSearchValues.slice(formSearchValues.length - 5);
    }
    localStorage.setItem('lastSearched', JSON.stringify(formSearchValues));
  }

  displayLastSearched() {
    const storedSearchValues = localStorage.getItem('lastSearched');
    const searchArray = JSON.parse(storedSearchValues);
    var listContainer = this.querySelector('[data-latest-searches]');
    const ul = document.createElement('ul');

    (listContainer || {}).innerHTML = '';

    (searchArray || []).forEach(function (value) {
      var li = document.createElement('li');
      let input = document.querySelector('#Search-In-Modal');
      li.textContent = value;

      li.addEventListener('click', () => {
        input.value = value.trim();
      });

      ul.appendChild(li);
    });

    if (listContainer) {
      listContainer.appendChild(ul);
    }
  }

  clearLastSearched() {
    const clearButton = document.querySelector('[data-clear-latest-searches]');
    if (clearButton) {
      clearButton.addEventListener('click', function () {
        localStorage.removeItem('lastSearched');
        const latestSearchesElement = document.querySelector('[data-latest-searches]');
        latestSearchesElement.innerHTML = '';
      });
    }
  }
}

customElements.define('predictive-search', PredictiveSearch);
